import {
    Bar,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    Tooltip,
    XAxis,
    YAxis,
    BarChart,
    ResponsiveContainer,
} from "recharts";

import Typography from "@/ui/atoms/Typography";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type { UserAnalytics } from "@/ui/organisms/dashboardTypes";
import { CHART_COLORS } from "@/ui/organisms/dashboardTypes";

import { createSortByNumberPropertyComparator } from "@/utils/sort";

import { months } from "../createViewDashboard";

const compareByValueDesc = createSortByNumberPropertyComparator(
    "value",
    "desc",
);

export const retrieveUserAnalyticsNodes = (
    userAnalytics: UserAnalytics,
    currentMonth: number,
) => {
    const usersByType = Object.entries(userAnalytics.usersByType)
        .map(([user, count]) => ({ name: user, value: count }))
        .toSorted(compareByValueDesc);

    const barChartData = [
        { name: "Total", value: userAnalytics.totalUsers },
        {
            name: "Last 7d",
            value: userAnalytics.newUsersLast7d,
        },
        {
            name: "Last 24h",
            value: userAnalytics.newUsersLast24h,
        },
    ];

    const lineData = Array.from({ length: 12 }, (_, i) => {
        const monthIndex = (currentMonth - (11 - i) + 12) % 12;
        return {
            month: months[monthIndex],
            value: userAnalytics.signupsOverTime[monthIndex + 1] ?? 0,
        };
    });

    return userAnalytics.totalUsers > 0
        ? [
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              User Types
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <PieChart>
                                  <Pie
                                      data={usersByType}
                                      dataKey="value"
                                      nameKey="name"
                                      outerRadius={100}
                                      fill="#8884d8"
                                  >
                                      {usersByType.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Pie>
                                  <Legend
                                      verticalAlign="bottom"
                                      height={36}
                                      align="right"
                                  />
                              </PieChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User Type Analytics",
                      w: 3,
                      h: 2,
                      x: 0,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Recent Signups
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart
                                  data={barChartData}
                                  width={500}
                                  height={300}
                              >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {barChartData.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User Signup Chart",
                      w: 3,
                      h: 2,
                      x: 3,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              User Signups over time
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <LineChart data={lineData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" />
                                  <YAxis />
                                  <Tooltip />
                                  <Line
                                      type="monotone"
                                      dataKey="value"
                                      stroke="#8884d8"
                                  />
                              </LineChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User Signups over time",
                      w: 6,
                      h: 2,
                      x: 0,
                      y: 2,
                  },
              },
          ]
        : [
              {
                  element: (
                      <LayoutFillParentCentered>
                          <Typography variant="h1">No Users Found</Typography>
                      </LayoutFillParentCentered>
                  ),
                  options: {
                      i: "No Users yet!",
                      w: 100,
                      h: 2,
                      x: 0,
                      y: 0,
                      boxed: false,
                  },
              },
          ];
};
